// src/pages/Profile.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Spinner } from "react-bootstrap";
import api, {
  createConjoint,
  createEnfant,
  createFamilyChangeRequest,
  createParent,
} from "../api/api";
import "./Profile.css";

const LOGO_SRC = "/logoamicale.png";

function safeValue(value, fallback = "-") {
  return value || fallback;
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return `${amount.toFixed(2)} DT`;
}

function initials(firstName, lastName) {
  const first = firstName?.trim()?.[0] || "";
  const last = lastName?.trim()?.[0] || "";
  return `${first}${last}`.toUpperCase() || "AD";
}

function InfoLine({ label, value }) {
  return (
    <div className="profile-info-line">
      <span>{label}</span>
      <strong>{safeValue(value)}</strong>
    </div>
  );
}

function StatCard({ label, value, helper, tone = "green" }) {
  return (
    <div className={`profile-stat profile-stat--${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {helper && <small>{helper}</small>}
    </div>
  );
}

function FamilyCard({ title, count, children }) {
  return (
    <section className="profile-card profile-family-card">
      <div className="profile-card-heading">
        <div>
          <span className="profile-eyebrow">Famille</span>
          <h3>{title}</h3>
        </div>
        <Badge bg="success">{count}</Badge>
      </div>
      <div className="profile-family-list">{children}</div>
    </section>
  );
}

function EmptyFamily({ label }) {
  return (
    <div className="profile-empty-family">
      <span>Aucun {label} ajoute.</span>
    </div>
  );
}

function Profile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [familyModal, setFamilyModal] = useState(null);
  const [familyForm, setFamilyForm] = useState({});
  const [requestTarget, setRequestTarget] = useState(null);
  const [requestMessage, setRequestMessage] = useState("");
  const [requestPayload, setRequestPayload] = useState({});
  const [requestFiles, setRequestFiles] = useState([]);
  const [savingFamily, setSavingFamily] = useState(false);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("adherents/profile/");
      setData(res.data);
    } catch (e) {
      setError("Impossible de charger le profil.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const parents = useMemo(() => (Array.isArray(data?.parents) ? data.parents : []), [data]);
  const enfants = useMemo(() => (Array.isArray(data?.enfants) ? data.enfants : []), [data]);
  const conjoint = data?.conjoint || null;
  const situation = data?.situation_familiale || "celibataire";
  const canAddChildDirectly = situation === "marie";
  const canRequestChildCreation = situation === "divorce";
  const canAddConjoint = situation === "marie";
  const fullName = `${data?.first_name || ""} ${data?.last_name || ""}`.trim() || "Adherent";
  const hasFather = parents.some((parent) => parent.type_parent === "pere");
  const hasMother = parents.some((parent) => parent.type_parent === "mere");

  const openFamilyModal = (type, initial = {}) => {
    setFamilyModal(type);
    setFamilyForm(initial);
  };

  const updateFamilyField = (field, value) => {
    setFamilyForm((prev) => ({ ...prev, [field]: value }));
  };

  const openCorrectionRequest = (type, item, label) => {
    const payload = {
      nom: item.nom || "",
      prenom: item.prenom || "",
      date_naissance: item.date_naissance || "",
    };
    if (type === "parent") {
      payload.type_parent = item.type_parent || "pere";
      payload.situation = item.situation || "vivant";
    }
    if (type === "enfant") {
      payload.sexe = item.sexe || "M";
      payload.classe = item.classe || "";
      payload.etablissement = item.etablissement || "";
    }
    if (type === "conjoint") {
      payload.cin = item.cin || "";
      payload.telephone = item.telephone || "";
    }

    setRequestTarget({ type, id: item.id, label });
    setRequestPayload(payload);
    setRequestMessage("");
    setRequestFiles([]);
    setError("");
  };

  const updateRequestPayload = (field, value) => {
    setRequestPayload((prev) => ({ ...prev, [field]: value }));
  };

  const handleFamilySubmit = async (event) => {
    event.preventDefault();
    if (familyModal === "enfant" && !canAddChildDirectly) {
      setError(
        canRequestChildCreation
          ? "Pour un adherent divorce, l'ajout d'enfant passe par une demande famille."
          : "Un adherent celibataire ne peut pas ajouter un enfant."
      );
      return;
    }
    if (familyModal === "conjoint" && !canAddConjoint) {
      setError("Seul un adherent marie peut ajouter un conjoint.");
      return;
    }
    if (familyForm.cin && !/^\d{8}$/.test(familyForm.cin)) {
      setError("CIN doit contenir exactement 8 chiffres.");
      return;
    }
    if (familyForm.telephone && !/^\d{8,15}$/.test(String(familyForm.telephone).replace(/\s/g, ""))) {
      setError("Telephone invalide: 8 a 15 chiffres attendus.");
      return;
    }
    if (familyForm.date_naissance && new Date(familyForm.date_naissance) > new Date()) {
      setError("Date naissance invalide: elle ne peut pas etre dans le futur.");
      return;
    }
    setSavingFamily(true);
    try {
      if (familyModal === "parent") {
        await createParent(familyForm);
      } else if (familyModal === "enfant") {
        await createEnfant(familyForm);
      } else if (familyModal === "enfant-request") {
        await createFamilyChangeRequest({
          target_type: "enfant",
          target_id: null,
          action: "create",
          message: "Demande d'ajout enfant",
          payload: familyForm,
        });
      } else if (familyModal === "conjoint") {
        await createConjoint(familyForm);
      }
      setFamilyModal(null);
      setFamilyForm({});
      window.dispatchEvent(new Event("amicale:notifications-refresh"));
      await loadProfile();
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible d'enregistrer la famille.");
    } finally {
      setSavingFamily(false);
    }
  };

  const submitCorrectionRequest = async (event) => {
    event.preventDefault();
    if (!requestTarget) return;
    if (requestFiles.length === 0) {
      setError("Ajoutez au moins une piece justificative pour la correction.");
      return;
    }
    setSavingFamily(true);
    try {
      const formData = new FormData();
      formData.append("target_type", requestTarget.type);
      if (requestTarget.id) formData.append("target_id", requestTarget.id);
      formData.append("action", requestTarget.action || "update");
      formData.append("message", requestMessage);
      formData.append("payload", JSON.stringify(requestTarget.payload || requestPayload));
      requestFiles.forEach((file) => formData.append("proof_files", file));
      await createFamilyChangeRequest(formData);
      setRequestTarget(null);
      setRequestMessage("");
      setRequestPayload({});
      setRequestFiles([]);
      window.dispatchEvent(new Event("amicale:notifications-refresh"));
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally {
      setSavingFamily(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <Spinner animation="border" />
        <div>Chargement du profil...</div>
      </div>
    );
  }

  if (error) return <Alert variant="danger" className="mt-4">{error}</Alert>;
  if (!data) return <Alert variant="warning" className="mt-4">Aucune donnee.</Alert>;

  return (
    <main className="profile-page">
      <section className="profile-hero">
        <div className="profile-hero-glow profile-hero-glow--one" />
        <div className="profile-hero-glow profile-hero-glow--two" />

        <div className="profile-brand-lockup">
          <div className="profile-logo-shell">
            <img src={LOGO_SRC} alt="Logo Amicale Autoroutes" />
          </div>
          <div>
            <span className="profile-eyebrow profile-eyebrow--light">Espace adherent</span>
            <h1>Mon profil</h1>
            <p>Une vue claire de vos informations, votre famille et votre quota amicale.</p>
          </div>
        </div>

        <div className="profile-identity-card">
          <div className="profile-avatar">{initials(data.first_name, data.last_name)}</div>
          <div className="profile-identity-main">
            <span>Bienvenue</span>
            <h2>{fullName}</h2>
            <div className="profile-badges">
              <Badge bg="light" text="dark">Matricule {safeValue(data.matricule)}</Badge>
              {data.is_amical_admin && <Badge bg="warning" text="dark">Admin amicale</Badge>}
              <Badge bg="success">Compte actif</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="profile-stats-grid">
        <StatCard label="Quota maximum" value={formatMoney(data.quota_max)} helper="Plafond autorise" />
        <StatCard label="Parents" value={parents.length} helper="Membres rattaches" tone="gold" />
        <StatCard label="Enfants" value={enfants.length} helper="Beneficiaires famille" tone="lime" />
      </section>

      <section className="profile-content-grid">
        <section className="profile-card profile-card--personal">
          <div className="profile-card-heading">
            <div>
              <span className="profile-eyebrow">Identite</span>
              <h3>Informations personnelles</h3>
            </div>
          </div>
          <InfoLine label="Nom complet" value={fullName} />
          <InfoLine label="Date naissance" value={data.date_naissance} />
          <InfoLine label="Situation familiale" value={data.situation_familiale} />
          <InfoLine label="Telephone" value={data.telephone} />
          <InfoLine label="Email" value={data.email || "Aucun email"} />
        </section>

        <section className="profile-card profile-card--work">
          <div className="profile-card-heading">
            <div>
              <span className="profile-eyebrow">Affectation</span>
              <h3>Situation professionnelle</h3>
            </div>
          </div>
          <InfoLine label="Fonction" value={data.fonction_nom} />
          <InfoLine label="Gare" value={data.gare_nom} />
          <InfoLine label="District" value={data.district_nom} />
          <InfoLine label="Quota max" value={formatMoney(data.quota_max)} />
        </section>
      </section>

      <section className="profile-content-grid profile-content-grid--family">
        <FamilyCard title="Parents" count={parents.length}>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {!hasFather && (
              <Button size="sm" variant="outline-success" onClick={() => openFamilyModal("parent", { type_parent: "pere", situation: "vivant" })}>
                Ajouter pere
              </Button>
            )}
            {!hasMother && (
              <Button size="sm" variant="outline-success" onClick={() => openFamilyModal("parent", { type_parent: "mere", situation: "vivant" })}>
                Ajouter mere
              </Button>
            )}
          </div>
          {parents.length === 0 ? (
            <EmptyFamily label="parent" />
          ) : (
            parents.map((parent) => (
              <article key={parent.id} className="profile-family-item">
                <div>
                  <strong>{parent.prenom} {parent.nom}</strong>
                  <span>{safeValue(parent.type_parent)} - {safeValue(parent.situation)}</span>
                </div>
                <Button size="sm" variant="outline-warning" onClick={() => openCorrectionRequest("parent", parent, `${parent.prenom} ${parent.nom}`)}>
                  Demander correction
                </Button>
              </article>
            ))
          )}
        </FamilyCard>

        <FamilyCard title="Enfants" count={enfants.length}>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {canAddChildDirectly && (
              <Button size="sm" variant="outline-success" onClick={() => openFamilyModal("enfant", { sexe: "M" })}>
                Ajouter enfant
              </Button>
            )}
            {canRequestChildCreation && (
              <Button
                size="sm"
                variant="outline-warning"
                onClick={() => openFamilyModal("enfant-request", { sexe: "M" })}
              >
                Demander ajout enfant
              </Button>
            )}
            {!canAddChildDirectly && !canRequestChildCreation && (
              <Alert variant="light" className="py-2 mb-0">
                Ajout enfant non disponible pour un adherent celibataire.
              </Alert>
            )}
          </div>
          {enfants.length === 0 ? (
            <EmptyFamily label="enfant" />
          ) : (
            enfants.map((enfant) => (
              <article key={enfant.id} className="profile-family-item">
                <div>
                  <strong>{enfant.prenom} {enfant.nom}</strong>
                  <span>
                    {enfant.age ?? "?"} ans
                    {enfant.classe ? ` - ${enfant.classe}` : ""}
                  </span>
                </div>
                <Button size="sm" variant="outline-warning" onClick={() => openCorrectionRequest("enfant", enfant, `${enfant.prenom} ${enfant.nom}`)}>
                  Demander correction
                </Button>
              </article>
            ))
          )}
        </FamilyCard>
      </section>

      <section className="profile-content-grid profile-content-grid--family">
        <FamilyCard title="Conjoint" count={conjoint ? 1 : 0}>
          {!conjoint ? (
            canAddConjoint ? (
              <Button size="sm" variant="outline-success" onClick={() => openFamilyModal("conjoint")}>
                Ajouter conjoint
              </Button>
            ) : (
              <Alert variant="light" className="py-2 mb-0">
                Ajout conjoint disponible seulement pour les adherents maries.
              </Alert>
            )
          ) : (
            <article className="profile-family-item">
              <div>
                <strong>{conjoint.prenom} {conjoint.nom}</strong>
                <span>{safeValue(conjoint.telephone)} - {safeValue(conjoint.cin)}</span>
              </div>
              <Button size="sm" variant="outline-warning" onClick={() => openCorrectionRequest("conjoint", conjoint, `${conjoint.prenom} ${conjoint.nom}`)}>
                Demander correction
              </Button>
            </article>
          )}
        </FamilyCard>
      </section>

      <FamilyModal
        type={familyModal}
        values={familyForm}
        saving={savingFamily}
        onChange={updateFamilyField}
        onHide={() => setFamilyModal(null)}
        onSubmit={handleFamilySubmit}
      />

      <CorrectionModal
        target={requestTarget}
        message={requestMessage}
        payload={requestPayload}
        files={requestFiles}
        saving={savingFamily}
        onChange={setRequestMessage}
        onPayloadChange={updateRequestPayload}
        onFilesChange={setRequestFiles}
        onHide={() => setRequestTarget(null)}
        onSubmit={submitCorrectionRequest}
      />
    </main>
  );
}

function FamilyModal({ type, values, saving, onChange, onHide, onSubmit }) {
  if (!type) return null;
  const isChildForm = type === "enfant" || type === "enfant-request";
  const title =
    type === "parent"
      ? "Ajouter parent"
      : type === "enfant-request"
      ? "Demande ajout enfant"
      : type === "enfant"
      ? "Ajouter enfant"
      : "Ajouter conjoint";

  return (
    <Modal show onHide={onHide} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {type === "parent" && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select value={values.type_parent || "pere"} onChange={(e) => onChange("type_parent", e.target.value)}>
                  <option value="pere">Pere</option>
                  <option value="mere">Mere</option>
                </Form.Select>
              </Form.Group>
              <CommonPersonFields values={values} onChange={onChange} />
              <Form.Group className="mb-3">
                <Form.Label>Situation</Form.Label>
                <Form.Select value={values.situation || "vivant"} onChange={(e) => onChange("situation", e.target.value)}>
                  <option value="vivant">Vivant</option>
                  <option value="decede">Decede</option>
                </Form.Select>
              </Form.Group>
            </>
          )}

          {isChildForm && (
            <>
              {type === "enfant-request" && (
                <Alert variant="warning">
                  Votre demande sera envoyee a l'admin amicale pour validation avant creation.
                </Alert>
              )}
              <CommonPersonFields values={values} onChange={onChange} />
              <Form.Group className="mb-3">
                <Form.Label>Sexe</Form.Label>
                <Form.Select value={values.sexe || "M"} onChange={(e) => onChange("sexe", e.target.value)}>
                  <option value="M">Masculin</option>
                  <option value="F">Feminin</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Classe</Form.Label>
                <Form.Control value={values.classe || ""} onChange={(e) => onChange("classe", e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Etablissement</Form.Label>
                <Form.Control value={values.etablissement || ""} onChange={(e) => onChange("etablissement", e.target.value)} />
              </Form.Group>
            </>
          )}

          {type === "conjoint" && (
            <>
              <CommonPersonFields values={values} onChange={onChange} />
              <Form.Group className="mb-3">
                <Form.Label>CIN</Form.Label>
                <Form.Control value={values.cin || ""} maxLength={8} onChange={(e) => onChange("cin", e.target.value.replace(/\D/g, ""))} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Telephone</Form.Label>
                <Form.Control value={values.telephone || ""} onChange={(e) => onChange("telephone", e.target.value)} />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" disabled={saving}>
            {saving ? "Enregistrement..." : type === "enfant-request" ? "Envoyer demande" : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CommonPersonFields({ values, onChange }) {
  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Nom</Form.Label>
        <Form.Control required value={values.nom || ""} onChange={(e) => onChange("nom", e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Prenom</Form.Label>
        <Form.Control required value={values.prenom || ""} onChange={(e) => onChange("prenom", e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Date naissance</Form.Label>
        <Form.Control type="date" value={values.date_naissance || ""} onChange={(e) => onChange("date_naissance", e.target.value)} />
      </Form.Group>
    </>
  );
}

function CorrectionModal({ target, message, payload, files, saving, onChange, onPayloadChange, onFilesChange, onHide, onSubmit }) {
  if (!target) return null;
  const isParent = target.type === "parent";
  const isChild = target.type === "enfant";
  const isConjoint = target.type === "conjoint";

  return (
    <Modal show onHide={onHide} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Demande correction famille</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="light">
            Modifiez les champs ci-dessous et joignez une preuve. L'admin appliquera la correction apres verification.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>{target.label}</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              required
              value={message}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Expliquez la correction souhaitee"
            />
          </Form.Group>

          {isParent && (
            <Form.Group className="mb-3">
              <Form.Label>Type parent</Form.Label>
              <Form.Select value={payload.type_parent || "pere"} onChange={(e) => onPayloadChange("type_parent", e.target.value)}>
                <option value="pere">Pere</option>
                <option value="mere">Mere</option>
              </Form.Select>
            </Form.Group>
          )}

          <CommonPersonFields values={payload} onChange={onPayloadChange} />

          {isParent && (
            <Form.Group className="mb-3">
              <Form.Label>Situation</Form.Label>
              <Form.Select value={payload.situation || "vivant"} onChange={(e) => onPayloadChange("situation", e.target.value)}>
                <option value="vivant">Vivant</option>
                <option value="decede">Decede</option>
              </Form.Select>
            </Form.Group>
          )}

          {isChild && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Sexe</Form.Label>
                <Form.Select value={payload.sexe || "M"} onChange={(e) => onPayloadChange("sexe", e.target.value)}>
                  <option value="M">Masculin</option>
                  <option value="F">Feminin</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Classe</Form.Label>
                <Form.Control value={payload.classe || ""} onChange={(e) => onPayloadChange("classe", e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Etablissement</Form.Label>
                <Form.Control value={payload.etablissement || ""} onChange={(e) => onPayloadChange("etablissement", e.target.value)} />
              </Form.Group>
            </>
          )}

          {isConjoint && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>CIN</Form.Label>
                <Form.Control value={payload.cin || ""} maxLength={8} onChange={(e) => onPayloadChange("cin", e.target.value.replace(/\D/g, ""))} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Telephone</Form.Label>
                <Form.Control value={payload.telephone || ""} onChange={(e) => onPayloadChange("telephone", e.target.value)} />
              </Form.Group>
            </>
          )}

          <Form.Group>
            <Form.Label>Pieces justificatives</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => onFilesChange(Array.from(e.target.files || []))}
            />
            {files.length > 0 && (
              <div className="text-muted small mt-2">
                {files.length} piece(s) jointe(s)
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button type="submit" disabled={saving}>{saving ? "Envoi..." : "Valider la demande"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default Profile;
